import { toUrlRid } from '../../utils/tenantPath';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from '../../routing';
import { adminApi, commandCenterApi } from '../../utils/api';
import { toast } from '../../components/Toast';
import { useAuth } from '../../context/AuthContext';
import Icon from '../../components/Icon';
import ListRow from '../../components/ListRow';
import { useIsMobile } from '../../utils/useIsMobile';
import { NetworkModal, RestaurantModal } from './modals';
import { LoadState, formatDate, useRefetchOnFocus } from './parts';
import { useConfirm } from '../../utils/useConfirm';

// לקוחות — restaurants and networks in ONE section, because to ops they are one
// mental object: who do we serve, and how are they grouped. The old admin page
// put them in separate cards, which forced you to hold restaurant ids in your
// head to read the networks table.
//
// The tab is a query param rather than a route: same objects, one lens, and the
// back button then returns you to the list rather than to the other tab.

function ConfigMarks({ config }) {
  // Three small marks instead of three columns. business_id is the one that
  // matters most: without it, matching an uploaded document to this restaurant
  // silently degrades to fuzzy name similarity, and that surfaces days later as
  // a mis-routed document rather than as an error.
  const marks = [
    { on: config.has_business_id, label: 'ח.פ.' },
    { on: config.has_dokka,       label: 'דוקה' },
    { on: config.payment_tracking, label: 'תשלומים' },
  ];
  return (
    <span style={{ display: 'inline-flex', gap: 5 }}>
      {marks.map(m => (
        <span key={m.label} className={`cc-pill${m.on ? ' ok' : ''}`} title={m.on ? 'מוגדר' : 'לא מוגדר'}>
          <Icon name={m.on ? 'check' : 'close'} size={11} /> {m.label}
        </span>
      ))}
    </span>
  );
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState(null);
  const [askConfirm, confirmDialog] = useConfirm();
  const [networks, setNetworks] = useState(null);
  const [error, setError] = useState(false);
  const [modal, setModal] = useState(null);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { refreshRestaurants } = useAuth();

  const tab = params.get('tab') === 'networks' ? 'networks' : 'restaurants';
  const highlight = params.get('highlight');
  const q = params.get('q') || '';

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setError(false);
    try {
      // Networks load in the same pass — but a failure here is REPORTED, not
      // swallowed into an empty list the way the old admin page did it.
      const [c, n] = await Promise.all([
        commandCenterApi.getCustomers(),
        adminApi.getNetworks(),
      ]);
      setCustomers(Array.isArray(c.data) ? c.data : []);
      setNetworks(Array.isArray(n.data) ? n.data : []);
    } catch {
      setError(true);
      setCustomers(null);
      setNetworks(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useRefetchOnFocus(load);

  const visibleCustomers = useMemo(() => {
    if (!customers) return [];
    const needle = q.trim();
    return needle ? customers.filter(c => c.name.includes(needle)) : customers;
  }, [customers, q]);

  const visibleNetworks = useMemo(() => {
    if (!networks) return [];
    const needle = q.trim();
    return needle ? networks.filter(n => n.name.includes(needle)) : networks;
  }, [networks, q]);

  const nameById = useMemo(
    () => new Map((customers || []).map(c => [c.id, c.name])),
    [customers],
  );

  const deleteNetwork = (n) => askConfirm({
    title: 'מחיקת רשת',
    message: `למחוק את הרשת "${n.name}"?`,
    detail: 'המסעדות עצמן לא יימחקו — הן רק יפסיקו להיות משויכות לרשת.',
    confirmLabel: 'מחק רשת',
    tone: 'danger',
    onConfirm: async () => {
      try {
        await adminApi.deleteNetwork(n.id);
        toast('הרשת נמחקה');
        load();
      } catch (err) {
        toast(err.response?.data?.error || 'שגיאה', 'error');
      }
    },
  });

  return (
    <>
      <h1 className="cc-page-title">לקוחות</h1>

      <div className="cc-card">
        <div className="cc-card-head" style={{ marginBottom: 0, flexWrap: 'wrap' }}>
          <div className="view-tabs" style={{ margin: 0 }}>
            <button className={`view-tab${tab === 'restaurants' ? ' is-active' : ''}`}
              onClick={() => setParam('tab', '')}>
              מסעדות {customers ? `(${customers.length})` : ''}
            </button>
            <button className={`view-tab${tab === 'networks' ? ' is-active' : ''}`}
              onClick={() => setParam('tab', 'networks')}>
              רשתות {networks ? `(${networks.length})` : ''}
            </button>
          </div>
          <input
            className="form-control"
            style={{ maxWidth: 220 }}
            value={q}
            onChange={e => setParam('q', e.target.value)}
            placeholder="סינון לפי שם"
            aria-label="סינון לפי שם"
          />
          <div className="cc-spacer" />
          {tab === 'restaurants' ? (
            <button className="btn btn-primary btn-sm" onClick={() => setModal({ type: 'restaurant' })}>
              <Icon name="plus" size={14} /> מסעדה חדשה
            </button>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={() => setModal({ type: 'network' })}>
              <Icon name="plus" size={14} /> רשת חדשה
            </button>
          )}
        </div>
      </div>

      <div className="cc-card">
        <LoadState
          loading={customers === null && !error}
          error={error}
          onRetry={load}
          empty={tab === 'restaurants' ? visibleCustomers.length === 0 : visibleNetworks.length === 0}
          emptyMessage={tab === 'restaurants'
            ? (customers?.length ? 'אין מסעדות שמתאימות לסינון' : 'אין מסעדות עדיין')
            : (networks?.length ? 'אין רשתות שמתאימות לסינון' : 'אין רשתות — צרו רשת כדי לקבל דוחות מאוחדים')}
        >
          {tab === 'restaurants' && isMobile ? (
            // Phone view of the customer list. Users and suppliers are the two
            // counts worth carrying at this width; the config marks and dates
            // live on the card, one tap away.
            <div className="m-list">
              {visibleCustomers.map(c => (
                <ListRow
                  key={c.id}
                  primary={c.name}
                  meta={
                    <>
                      <span>מספר לקוח {toUrlRid(c.id)}</span>
                      {!!c.is_demo && <span className="cc-pill">דמו</span>}
                      {!c.config.has_business_id && <span className="cc-pill bad">חסר ח.פ.</span>}
                      {c.network_name && <span>{c.network_name}</span>}
                      <span>{c.user_count} משתמשים</span>
                      <span>·</span>
                      <span>{c.supplier_count} ספקים</span>
                    </>
                  }
                  onClick={() => navigate(`/command-center/customers/${c.id}`)}
                />
              ))}
            </div>
          ) : tab === 'restaurants' ? (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>שם</th>
                    <th>מספר לקוח</th>
                    <th>ח.פ.</th>
                    <th>רשת</th>
                    <th>משתמשים</th>
                    <th>ספקים</th>
                    <th>הגדרות</th>
                    <th>נוצרה</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibleCustomers.map(c => (
                    <tr key={c.id} className="cc-row-link"
                      onClick={() => navigate(`/command-center/customers/${c.id}`)}>
                      <td style={{ fontWeight: 600 }}>
                        {c.name}
                        {/* Marked here because a demo tenant is deliberately
                            missing from the queues and the overview totals —
                            without the badge that reads as data going astray. */}
                        {!!c.is_demo && <span className="cc-pill" style={{ marginInlineStart: 6 }}>דמו</span>}
                      </td>
                      <td><bdi>{toUrlRid(c.id)}</bdi></td>
                      <td className="cc-mono">
                        {c.business_id || <span style={{ color: '#b91c1c' }}>חסר</span>}
                      </td>
                      <td>{c.network_name || <span className="cc-muted">—</span>}</td>
                      {/* Clickable, unlike the old dead-end count — and it
                          counts every role rather than silently excluding admins. */}
                      <td className="cc-num">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={(e) => { e.stopPropagation(); navigate(`/command-center/people?restaurant=${c.id}`); }}
                        >
                          {c.user_count}
                        </button>
                      </td>
                      <td className="cc-num">{c.supplier_count}</td>
                      <td><ConfigMarks config={c.config} /></td>
                      <td className="cc-muted cc-num">{formatDate(c.created_at)}</td>
                      <td className="cc-go"><Icon name="chevron-inline" size={14} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>שם הרשת</th><th>מסעדות</th><th /></tr>
                </thead>
                <tbody>
                  {visibleNetworks.map(n => {
                    const names = (n.restaurant_ids || []).map(id => nameById.get(id)).filter(Boolean);
                    return (
                      <tr key={n.id}
                        style={String(n.id) === highlight ? { background: 'var(--peak-sage-bar)' } : undefined}>
                        <td style={{ fontWeight: 600 }}>{n.name}</td>
                        <td>
                          {names.length
                            ? <><span className="cc-num">{names.length}</span> · {names.join(', ')}</>
                            : <span className="cc-muted">—</span>}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-sm btn-secondary"
                              onClick={() => setModal({ type: 'network', network: n })}>עריכה</button>
                            <button className="btn btn-sm btn-danger"
                              onClick={() => deleteNetwork(n)}>מחק</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </LoadState>
      </div>

      {modal?.type === 'restaurant' && (
        <RestaurantModal
          restaurant={modal.restaurant || null}
          onSave={() => { setModal(null); load(); refreshRestaurants(); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'network' && (
        <NetworkModal
          network={modal.network || null}
          restaurants={customers || []}
          onSave={() => { setModal(null); load(); }}
          onClose={() => setModal(null)}
        />
      )}
      {confirmDialog}
    </>
  );
}
